import {useCallback, useEffect, useRef, useState} from 'react'
import {Pressable, StyleSheet, useWindowDimensions, View} from 'react-native'
import {SystemBars} from 'react-native-edge-to-edge'
import {useReducedMotion} from 'react-native-reanimated'
import {useSafeAreaInsets} from 'react-native-safe-area-context'
import {Trans, useLingui} from '@lingui/react/macro'
import {useFocusEffect} from '@react-navigation/native'

import {getVideoThumbnail} from '#/lib/media/util'
import {
  type CommonNavigatorParams,
  type NativeStackScreenProps,
} from '#/lib/routes/types'
import {FeedFeedbackProvider} from '#/state/feed-feedback'
import {useEnableMinimalShellModeForScreen} from '#/state/shell'
import {VideoItem} from '#/screens/VideoFeed'
import {atoms as a, ThemeProvider, useBreakpoints, useTheme} from '#/alf'
import {setSystemUITheme} from '#/alf/util/systemUI'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {CapsuleThemePlayer} from '#/components/capsules/CapsuleThemePlayer'
import {LikeHearts} from '#/components/capsules/LikeHearts'
import {
  ChevronBottom_Stroke2_Corner0_Rounded as ChevronBottom,
  ChevronTop_Stroke2_Corner0_Rounded as ChevronTop,
} from '#/components/icons/Chevron'
import {TimesLarge_Stroke2_Corner0_Rounded as X} from '#/components/icons/Times'
import * as Layout from '#/components/Layout'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'
import {IS_WEB} from '#/env'
import {
  AdjacentPageFront,
  PageChrome,
  PosterPage,
} from './components/PageChrome'
import {VerticalPager} from './components/VerticalPager'
import {useCapsulePlayController} from './controller'
import {
  type LoadIntent,
  type MediaRole,
  networkProfileFromHints,
  probeAllowed,
  roleLoadIntent,
} from './mediaPolicy'
import {type FeedVideo} from './types'
import {useNetworkHints} from './useNetworkProfile'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'CapsulePlay'>

/**
 * Capsule player shell — layout and providers only. All policy lives in the
 * controller, all media mechanics in the engine, and the active-page chrome
 * in PageChrome; this file wires them together (pager pages, prompt, quiz
 * host, back button, desktop arrows).
 *
 * Ownership map (capsule player):
 *  - engine.ts                  media element/player + platform seam
 *  - controller.ts              feed/index/mode/navigation policy + actions
 *  - components/PageChrome.tsx  active-page chrome presentation
 *  - index.tsx                  thin shell: layout, pager, prompt/quiz (this file)
 */
export function CapsulePlayScreen({route, navigation}: Props) {
  const {t: l} = useLingui()
  const t = useTheme()
  const {gtMobile} = useBreakpoints()
  useEnableMinimalShellModeForScreen()
  const insets = useSafeAreaInsets()
  const {width: windowWidth} = useWindowDimensions()
  const reducedMotion = useReducedMotion()
  const reducedMotionRef = useRef(false)
  useEffect(() => {
    reducedMotionRef.current = reducedMotion
  }, [reducedMotion])

  useFocusEffect(
    useCallback(() => {
      setSystemUITheme('lightbox', t)
      return () => {
        setSystemUITheme('theme', t)
      }
    }, [t]),
  )

  const c = useCapsulePlayController(route, navigation)

  // One-shot flying-hearts celebration when a capsule is liked: each burst is
  // anchored at the Like tap (page coordinates), re-anchored into the video
  // column's local space — the column is centered once the window is wider
  // than 600px. Bursts expire themselves after the animation finishes.
  const [likeBursts, setLikeBursts] = useState<
    {id: number; x: number; y: number}[]
  >([])
  const likeBurstSeq = useRef(0)
  const likeBurstTimers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set())
  useEffect(() => {
    const timers = likeBurstTimers.current
    return () => {
      timers.forEach(clearTimeout)
      timers.clear()
    }
  }, [])
  const onLikeCelebrated = useCallback(
    (pageX: number, pageY: number) => {
      if (!Number.isFinite(pageX) || !Number.isFinite(pageY)) return
      const columnOffsetX = Math.max(0, (windowWidth - 600) / 2)
      const x = pageX - columnOffsetX
      const y = pageY
      if (x < 0 || y < 0) return
      const id = ++likeBurstSeq.current
      setLikeBursts(prev => [...prev, {id, x, y}].slice(-4))
      const timer = setTimeout(() => {
        likeBurstTimers.current.delete(timer)
        setLikeBursts(prev => prev.filter(b => b.id !== id))
      }, 1900)
      likeBurstTimers.current.add(timer)
    },
    [windowWidth],
  )

  // Wheel/keyboard/etc. navigate one capsule at a time on desktop. On touch,
  // the pager scrolls physically — arrows are a desktop affordance.
  const showArrows = !c.pagerLocked && !c.isShowingQuiz && IS_WEB && gtMobile

  // ── Media preload window policy (web) ────────────────────────────────────
  // The browser's network hints (effectiveType / downlink / saveData) are
  // hints only — mediaPolicy clamps them into a coarse profile that decides
  // how much the prefetch window may speculate. The N+2 probe tier exists
  // only on fast links (bounded to one startup segment), and it follows the
  // direction the user is traveling: probing ahead of a backward move would
  // be pure waste. Native keeps its own one-next preload (engine.preloadNext)
  // and never grows the window.
  const networkHints = useNetworkHints()
  const networkProfile = networkProfileFromHints(networkHints)
  const lastIndexRef = useRef(c.currentIndex)
  const travelDirRef = useRef<1 | -1>(1)
  useEffect(() => {
    if (c.currentIndex === lastIndexRef.current) return
    travelDirRef.current = c.currentIndex > lastIndexRef.current ? 1 : -1
    lastIndexRef.current = c.currentIndex
  }, [c.currentIndex])
  const probeEnabled = IS_WEB && probeAllowed(networkProfile)
  const probeIndex =
    probeEnabled && c.capsuleFoundInFeed && c.videos.length > 0
      ? Math.max(
          0,
          Math.min(
            c.videos.length - 1,
            c.currentIndex + (travelDirRef.current === 1 ? 2 : -2),
          ),
        )
      : -1

  // ── Pager pages ───────────────────────────────────────────────────────────
  // found=false: a single page with the direct post. found=true: one page per
  // feed capsule at its natural scroll offset (pager index == feed index), so
  // the scroll container and the active index can never drift apart. The
  // ACTIVE page plays; its ±1 neighbors carry real (role-gated, bounded)
  // preload; a direction probe two steps ahead is added on fast links; every
  // other page is a lightweight poster. The neighbor/probe pages also render
  // their capsule front up front (AdjacentPageFront) so a scroll into them
  // shows a complete page — data and buttons already there, nothing pops in
  // after the pager commits.
  const makePage = (
    item: FeedVideo,
    active: boolean,
    role: MediaRole,
    loadIntent: LoadIntent,
  ) => {
    const chrome = active ? (
      <PageChrome
        item={item}
        overlayMode={c.overlayMode}
        capsuleModel={c.capsuleModel}
        videoPaused={c.videoPaused}
        hintSeconds={c.hintSeconds}
        autoAdvancePossible={c.autoAdvancePossible}
        skipStage={c.skipStage}
        skipCountdown={c.skipCountdown}
        skipProgress={c.skipProgress}
        progress={c.progress}
        videoReady={c.videoReady}
        insetsTop={insets.top}
        onSeek={c.onSeek}
        onTogglePause={c.onTogglePause}
        onLongPressChange={c.onLongPressChange}
        onPlayFromEnded={c.onPlayFromEnded}
        onSkip={c.onSkip}
        onPressPlayCapsule={c.onPressPlayCapsule}
        onAudioStateChange={c.onAudioStateChange}
        displayMuted={c.mutedPolicy}
        onLikeCelebrated={onLikeCelebrated}
      />
    ) : (
      <AdjacentPageFront item={item} />
    )

    return (
      <View
        key={item.post.uri}
        style={[StyleSheet.absoluteFill, {backgroundColor: '#000'}]}>
        <VideoItem
          player={active && !IS_WEB ? c.engine.player : undefined}
          post={item.post}
          embed={item.video}
          active={active && !!c.activeVideo}
          adjacent={false}
          scrollGesture={undefined}
          moderation={item.moderation}
          feedContext={item.feedContext}
          reqId={item.reqId}
          hideOverlay
          shouldPlay={active}
          onError={active ? c.onVideoError : undefined}
          {...(IS_WEB && active
            ? {videoRef: c.engine.webVideoRef, muted: c.mutedPolicy}
            : {})}
          {...(IS_WEB ? {role, loadIntent} : {})}
        />
        {chrome}
      </View>
    )
  }

  const pages: React.ReactNode[] = []
  if (c.capsuleFoundInFeed && c.videos.length > 0) {
    for (let j = 0; j < c.videos.length; j++) {
      const dist = Math.abs(j - c.currentIndex)
      if (dist === 0) {
        pages.push(makePage(c.videos[j], true, 'active', 'full'))
      } else if (dist === 1) {
        const role: MediaRole = j < c.currentIndex ? 'previous' : 'next'
        pages.push(
          makePage(
            c.videos[j],
            false,
            role,
            roleLoadIntent(role, networkProfile),
          ),
        )
      } else if (j === probeIndex) {
        pages.push(
          makePage(
            c.videos[j],
            false,
            'probe',
            roleLoadIntent('probe', networkProfile),
          ),
        )
      } else {
        pages.push(
          <PosterPage
            key={c.videos[j].post.uri}
            thumbnail={getVideoThumbnail(c.videos[j].video)}
          />,
        )
      }
    }
  } else if (c.activeVideo) {
    pages.push(makePage(c.activeVideo, true, 'active', 'full'))
  }

  return (
    <ThemeProvider theme="dark">
      <FeedFeedbackProvider value={c.feedFeedback}>
        <Layout.Screen
          noInsetTop
          style={{backgroundColor: 'black', flex: 1, overflow: 'hidden'}}>
          <SystemBars style={{statusBar: 'light', navigationBar: 'light'}} />
          {!c.isShowingQuiz && !c.isPromptOpen && (
            <View
              style={[a.absolute, a.z_50, {top: insets.top + 16, left: 16}]}>
              <Button
                label={l`Back`}
                onPress={c.isPlayingMode ? c.pauseAndAsk : c.exitPlayer}
                size="large"
                variant="solid"
                color="secondary"
                shape="round"
                style={[
                  {
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    width: 44,
                    height: 44,
                  },
                ]}>
                <ButtonIcon icon={X} size="lg" />
              </Button>
            </View>
          )}

          <View
            style={[
              a.flex_1,
              a.align_center,
              {backgroundColor: 'black', width: '100%'},
            ]}>
            {/* The 600px video column: pager + prompt + quiz live here so the
                overlays cover exactly the video surface. */}
            <View
              style={[a.relative, a.flex_1, {width: '100%', maxWidth: 600}]}>
              {c.playerHasNoVideo ? (
                <View
                  style={[
                    a.flex_1,
                    a.align_center,
                    a.justify_center,
                    {padding: 32},
                  ]}>
                  <View style={[a.align_center, a.gap_md, {maxWidth: 420}]}>
                    <Text
                      style={[
                        a.text_lg,
                        a.font_bold,
                        {color: '#fff', textAlign: 'center'},
                      ]}>
                      <Trans>This capsule can't be played</Trans>
                    </Text>
                    <Text
                      style={[
                        a.text_sm,
                        {
                          color: 'rgba(255,255,255,0.72)',
                          textAlign: 'center',
                        },
                      ]}>
                      {c.autoAdvancePossible ? (
                        <Trans>
                          Its video may still be processing or may have been
                          removed. Go to the next capsule or close this one.
                        </Trans>
                      ) : (
                        <Trans>
                          Its video may still be processing or may have been
                          removed. Close this one and come back later.
                        </Trans>
                      )}
                    </Text>
                    <Button
                      label={l`Next capsule`}
                      onPress={c.escapeToFeed}
                      disabled={!c.autoAdvancePossible}
                      size="small"
                      variant="solid"
                      color="secondary_inverted"
                      style={[a.rounded_full, {paddingHorizontal: 20}]}>
                      <ButtonText style={[a.font_bold]}>
                        <Trans>Next capsule</Trans>
                      </ButtonText>
                    </Button>
                  </View>
                </View>
              ) : pages.length === 0 ? (
                <View
                  style={[
                    a.flex_1,
                    a.align_center,
                    a.justify_center,
                    {padding: 32},
                  ]}>
                  <Loader size="xl" />
                </View>
              ) : (
                <VerticalPager
                  index={c.capsuleFoundInFeed ? c.currentIndex : 0}
                  total={pages.length}
                  disabled={c.pagerLocked}
                  onChange={c.onPagerPageChange}
                  pages={pages}
                  smooth={!reducedMotionRef.current}
                />
              )}

              {c.isPromptOpen && (
                <View style={[a.absolute, a.inset_0, {zIndex: 60}]}>
                  <Pressable
                    accessibilityLabel={l`Resume video`}
                    accessibilityHint={l`Dismisses this dialog and keeps watching`}
                    onPress={c.resumeVideo}
                    style={[
                      StyleSheet.absoluteFill,
                      {backgroundColor: 'rgba(0, 0, 0, 0.72)'},
                    ]}
                  />
                  <View
                    style={[
                      a.flex_1,
                      a.align_center,
                      a.justify_center,
                      {paddingHorizontal: 24},
                    ]}>
                    <View
                      style={{
                        width: '100%',
                        maxWidth: 420,
                        borderRadius: 20,
                        padding: 22,
                        backgroundColor: 'rgba(22, 22, 26, 0.97)',
                      }}>
                      <Text
                        style={[a.font_bold, {color: '#fff', fontSize: 17}]}>
                        <Trans>Leave the capsule?</Trans>
                      </Text>
                      <Text
                        style={[
                          a.text_sm,
                          {color: 'rgba(255, 255, 255, 0.78)', marginTop: 4},
                        ]}>
                        <Trans>
                          Did you tap by mistake? Your place in the video is
                          kept — continue watching or exit.
                        </Trans>
                      </Text>
                      <View style={[a.flex_row, a.gap_md, {marginTop: 18}]}>
                        <Button
                          label={l`Continue watching`}
                          onPress={c.resumeVideo}
                          size="large"
                          variant="solid"
                          color="primary"
                          style={[a.flex_1, a.rounded_full]}>
                          <ButtonText style={[a.font_bold]}>
                            <Trans>Continue watching</Trans>
                          </ButtonText>
                        </Button>
                        <Button
                          label={l`Exit`}
                          onPress={c.exitPlayer}
                          size="large"
                          variant="solid"
                          color="secondary"
                          style={[a.flex_1, a.rounded_full]}>
                          <ButtonText style={[a.font_bold]}>
                            <Trans>Exit</Trans>
                          </ButtonText>
                        </Button>
                      </View>
                    </View>
                  </View>
                </View>
              )}

              {/* CapsuleThemePlayer — shown when the video ends (mode B) or
                  on Skip. For html-bridge themes wait until the raw-record
                  query has settled so behaviorSettings.htmlThemeUrl is present
                  from the very first render (avoids "No HTML theme
                  configured"). */}
              {c.isShowingQuiz &&
                c.capsuleModel &&
                (c.capsuleModel.themeId !== 'html-bridge' ||
                  c.isRawCapsuleFetched) && (
                  <View style={[StyleSheet.absoluteFill, {zIndex: 40}]}>
                    <CapsuleThemePlayer
                      capsule={c.capsuleModel}
                      insetsTop={insets.top}
                      onClose={c.closeQuiz}
                    />
                  </View>
                )}

              {/* Flying-hearts like celebration — one-shot bursts anchored at
                  the Like tap, rendered over the video surface. */}
              {likeBursts.length > 0 && (
                <View
                  pointerEvents="box-none"
                  style={[a.absolute, a.inset_0, {zIndex: 45}]}>
                  {likeBursts.map(b => (
                    <LikeHearts key={b.id} x={b.x} y={b.y} />
                  ))}
                </View>
              )}
            </View>

            {/* Navigation Arrows — desktop (web) fallback (wheel/keys/pager).
                Hidden on mobile/touch — the pager scrolls there. */}
            {showArrows && (
              <View
                style={[
                  a.absolute,
                  {left: '50%', marginLeft: 300 + 16},
                  {top: '50%', marginTop: -60},
                  a.z_50,
                  a.gap_md,
                ]}>
                <Button
                  label={l`Previous`}
                  onPress={c.onPreviousCapsule}
                  disabled={c.currentIndex === 0}
                  size="large"
                  variant="solid"
                  color="secondary"
                  shape="round"
                  style={[
                    {
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      width: 44,
                      height: 44,
                    },
                    c.currentIndex === 0 && {opacity: 0.5},
                  ]}>
                  <ButtonIcon icon={ChevronTop} size="lg" />
                </Button>
                <Button
                  label={l`Next`}
                  onPress={c.onNextCapsule}
                  disabled={
                    c.capsuleFoundInFeed
                      ? c.currentIndex === c.videos.length - 1 && !c.hasNextPage
                      : true
                  }
                  size="large"
                  variant="solid"
                  color="secondary"
                  shape="round"
                  style={[
                    {
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      width: 44,
                      height: 44,
                    },
                    c.capsuleFoundInFeed &&
                      c.currentIndex === c.videos.length - 1 &&
                      !c.hasNextPage && {opacity: 0.5},
                  ]}>
                  <ButtonIcon icon={ChevronBottom} size="lg" />
                </Button>
              </View>
            )}
          </View>
        </Layout.Screen>
      </FeedFeedbackProvider>
    </ThemeProvider>
  )
}
